/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { assetUrls } from '@/configs/urls'
import { formatEmptyValue } from '@/utils/assets'
import {
  createId,
  normalizeValueArrayObjectToString,
  normalizeValueArrayToString
} from '@/utils/general'
import { getBatchNames, getFileTypeIconUrl, setPreviewUrl } from '@/utils/items'

export const useAssets = () => {
  const getCommonValues = (item) => {
    const assetId = item.assetId
    const creators = formatEmptyValue(
      normalizeValueArrayObjectToString(item.creators, 'name')
    )
    const isListed = item.listed
    const buttonLabel = 'Manage'
    const publishers = formatEmptyValue(
      normalizeValueArrayObjectToString(item.publishers, 'name')
    )
    const fileType = formatEmptyValue(
      normalizeValueArrayToString(item.fileType)
    )
    const url = `/${assetUrls.base}/${item.id}`
    const title = item.title
    const earned = 0
    const assetLicensingBatch = getBatchNames({
      containerValue: item.licensedAssets,
      batchKey: 'assetsLicensesBatch',
      batchNameKey: 'name'
    })
    return {
      assetBatch: formatEmptyValue(item.assetBatch?.name),
      assetId,
      assetLicensingBatch,
      buttonLabel,
      creators,
      earned,
      fileType,
      isListed,
      publishers,
      title,
      url
    }
  }

  const getCardProps = (item) => {
    const {
      assetBatch,
      assetId,
      assetLicensingBatch,
      buttonLabel,
      creators,
      earned,
      fileType,
      isListed,
      publishers,
      title,
      url
    } = getCommonValues(item)

    const currentEarned = isListed ? [{ value: `$ ${earned} earned` }] : []
    const details = [
      {
        label: 'Publisher',
        value: publishers
      },
      {
        label: 'Assets Type',
        value: fileType
      },
      {
        label: 'Asset ID',
        value: assetId
      },
      {
        label: 'Registration Batch',
        value: assetBatch
      },
      {
        label: 'Asset Licensing Batch',
        value: assetLicensingBatch
      },
      ...currentEarned
    ]

    return {
      buttonLabel,
      creators,
      details,
      imageUrl: setPreviewUrl(item.coverUrl, fileType),
      isLink: item.licensedAsset !== null,
      isListed,
      key: createId(),
      title,
      url
    }
  }

  const getItemsPropsForTable = (item) => {
    const {
      assetBatch,
      assetId,
      assetLicensingBatch,
      buttonLabel,
      creators,
      earned,
      fileType,
      isListed,
      publishers,
      title,
      url
    } = getCommonValues(item)

    return {
      assetBatch,
      assetId,
      assetLicensingBatch,
      buttonLabel,
      creators,
      earned,
      fileType,
      iconUrl: getFileTypeIconUrl(fileType),
      listed: isListed,
      publishers,
      title,
      url
    }
  }

  const tableColumns = [
    {
      accessorKey: 'icon',
      enableHiding: false,
      header: ''
    },
    {
      accessorKey: 'assetId',
      enableHiding: false,
      header: 'Asset Id'
    },
    {
      accessorKey: 'title',
      enableHiding: false,
      header: 'Title'
    },
    {
      accessorKey: 'earned',
      enableHiding: false,
      header: 'Earned'
    },
    {
      accessorKey: 'listed',
      enableHiding: false,
      header: 'Listed'
    },
    {
      accessorKey: 'creators',
      header: 'Creators'
    },
    {
      accessorKey: 'publishers',
      header: 'Publishers'
    },
    {
      accessorKey: 'fileType',
      header: 'File Type'
    },
    {
      accessorKey: 'assetBatch',
      header: 'Registration Batch'
    },
    {
      accessorKey: 'assetLicensingBatch',
      header: 'Asset Licensing Batch'
    },
    {
      accessorKey: 'actions',
      enableHiding: false,
      header: ''
    }
  ]
  const defaultHiddenColumns = [
    'creators',
    'publishers',
    'fileType',
    'assetBatch',
    'assetLicensingBatch'
  ]

  const tableInitialState = {
    columnVisibility: Object.fromEntries(
      defaultHiddenColumns.map((id) => [id, false])
    )
  }

  return {
    getCardProps,
    getItemsPropsForTable,
    tableColumns,
    tableInitialState
  }
}
